import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postAvailability } from '../lib/api/client';
import { US_TIMEZONES, browserDefaultTz } from '../lib/timezones';
import type {
  AvailabilityRequest,
  AvailabilitySlot,
  QualifySuccess,
} from '../lib/api/types';

interface Props {
  match: QualifySuccess;
  onSelectSlot: (slot: AvailabilitySlot, tz: string) => void;
  onBack: () => void;
  /** Forward booking window in days (default 30). */
  windowDays?: number;
  /** Meeting type slug forwarded to /api/availability for horizon caps. */
  meetingTypeSlug?: string;
  /** Meeting length in minutes forwarded to /api/availability (60/120/180). */
  durationMinutes?: number;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; slots: AvailabilitySlot[] }
  | { status: 'error'; message: string };

interface DayGroup {
  dayKey: string; // YYYY-MM-DD in advisor TZ
  dayLabel: string; // "Wed, Apr 29"
  slots: Array<{ slot: AvailabilitySlot; timeLabel: string }>;
}

function groupByDay(slots: AvailabilitySlot[], tz: string): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  const dateFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const dayLabelFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  for (const slot of slots) {
    const d = new Date(slot.startUtc);
    const key = dateFmt.format(d);
    let group = groups.get(key);
    if (!group) {
      group = {
        dayKey: key,
        dayLabel: dayLabelFmt.format(d),
        slots: [],
      };
      groups.set(key, group);
    }
    group.slots.push({ slot, timeLabel: timeFmt.format(d) });
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.dayKey.localeCompare(b.dayKey)
  );
}

export function SlotPicker({ match, onSelectSlot, onBack, windowDays = 30, meetingTypeSlug, durationMinutes }: Props) {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const [activeMonth, setActiveMonth] = useState<string | null>(null);
  // Background-refresh affordances. `refreshing` drives the Refresh button
  // state; `lastUpdated` powers the "Updated just now" freshness hint.
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // Display timezone for the slot list. Defaults to the visitor's zone;
  // toggling it re-renders every slot time and is sent as the booking's
  // prospect timezone. Scheduling itself is unaffected (slots are absolute).
  const [tz, setTz] = useState<string>(() => browserDefaultTz());

  // Two modes:
  //   - advisor_first: query availability for the single named advisor.
  //     The header reads "Pick a time with {firstName}" (the prospect was
  //     already introduced to that advisor — Lead Owner or Existing Client).
  //   - slot_first:    query the union of free slots across the eligible
  //     pool. The header reads just "Pick a time" — the assigned advisor
  //     is revealed on the post-booking confirmation screen, not here.
  const isAdvisorFirst = match.kind === 'advisor_first';

  // Stable key describing the exact availability query. Changes only when
  // the underlying query changes (advisor / pool / lead time / window /
  // meeting type) — used to re-create the loader and trigger a fresh load.
  const fetchKey = isAdvisorFirst
    ? `a:${match.advisor.id}`
    : `p:${match.eligibleAdvisorIds.join(',')}`;

  // Monotonic request id. Guards against an older in-flight fetch resolving
  // after a newer one (focus refetch racing the poll, or a re-key) and
  // clobbering fresh slots with stale data.
  const reqSeq = useRef(0);

  const loadSlots = useCallback(
    (mode: 'initial' | 'refresh' = 'initial') => {
      const seq = ++reqSeq.current;
      if (mode === 'initial') {
        setLoad({ status: 'loading' });
      } else {
        setRefreshing(true);
      }
      const reqBody: AvailabilityRequest = isAdvisorFirst
        ? {
            advisorId: match.advisor.id,
            leadTimeMinutes: match.effectiveLeadTimeMinutes,
            windowDays,
            meetingTypeSlug,
            durationMinutes,
          }
        : {
            eligibleAdvisorIds: match.eligibleAdvisorIds,
            leadTimeMinutes: match.effectiveLeadTimeMinutes,
            windowDays,
            meetingTypeSlug,
            durationMinutes,
          };
      postAvailability(reqBody)
        .then((res) => {
          if (seq !== reqSeq.current) return; // superseded by a newer request
          if (res.ok) {
            setLoad({ status: 'ok', slots: res.slots });
            setLastUpdated(Date.now());
          } else if (mode === 'initial') {
            // On a background refresh keep the existing slots rather than
            // blanking the picker over a transient error.
            setLoad({ status: 'error', message: res.message });
          }
        })
        .catch(() => {
          if (seq !== reqSeq.current) return;
          if (mode === 'initial') {
            setLoad({
              status: 'error',
              message: 'Network error loading availability',
            });
          }
        })
        .finally(() => {
          if (seq === reqSeq.current) setRefreshing(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchKey, match.effectiveLeadTimeMinutes, windowDays, meetingTypeSlug, durationMinutes]
  );

  // Initial load + reload whenever the query changes.
  useEffect(() => {
    loadSlots('initial');
  }, [loadSlots]);

  // Availability is only as fresh as the last fetch, and the booking
  // workspace is long-lived: an advisor can free up time on their Google
  // Calendar, then return to this tab minutes later expecting the new slot.
  // Keep the list live without a manual reload —
  //   - refetch when the tab regains focus / becomes visible, and
  //   - poll every 60s while mounted.
  // Both are background refreshes that never blank the grid or drop the
  // user's day/month selection.
  useEffect(() => {
    function refreshIfVisible() {
      if (document.visibilityState === 'visible') loadSlots('refresh');
    }
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    const interval = window.setInterval(refreshIfVisible, 60_000);
    return () => {
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.clearInterval(interval);
    };
  }, [loadSlots]);

  const days = useMemo(() => {
    if (load.status !== 'ok') return [];
    return groupByDay(load.slots, tz);
  }, [load, tz]);

  // Month-jump rail — only for extended windows (annuity-anniversary
  // looks 13 months out). Standard 30-day pickers keep the flat day rail.
  const showMonthRail = windowDays > 60;
  const months = useMemo(() => {
    if (!showMonthRail) return [];
    const out: Array<{ key: string; label: string; dayCount: number }> = [];
    for (const day of days) {
      const key = day.dayKey.slice(0, 7); // YYYY-MM in advisor TZ
      const last = out[out.length - 1];
      if (last && last.key === key) {
        last.dayCount += 1;
        continue;
      }
      const [y, m] = key.split('-').map(Number);
      if (!y || !m) continue;
      const label = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(y, m - 1, 1)));
      out.push({ key, label, dayCount: 1 });
    }
    return out;
  }, [days, showMonthRail]);

  const visibleDays =
    showMonthRail && activeMonth
      ? days.filter((d) => d.dayKey.startsWith(activeMonth))
      : days;

  // Auto-select the first month + day with slots once data arrives
  useEffect(() => {
    if (days.length === 0) return;
    if (showMonthRail && activeMonth === null) {
      setActiveMonth(days[0].dayKey.slice(0, 7));
    }
    if (activeDay === null) {
      setActiveDay(days[0].dayKey);
    }
  }, [days, activeDay, activeMonth, showMonthRail]);

  // Jump to a month: filter the day rail and land on its first open day.
  function selectMonth(key: string) {
    setActiveMonth(key);
    const first = days.find((d) => d.dayKey.startsWith(key));
    setActiveDay(first ? first.dayKey : null);
  }

  const activeGroup = days.find((d) => d.dayKey === activeDay);

  const titleText = isAdvisorFirst
    ? `Pick a time with ${match.advisor.firstName}`
    : 'Pick a time';
  const subText = isAdvisorFirst
    ? `${match.meetingDurationMinutes}-minute Zoom call · All times shown in your time zone`
    : `${match.meetingDurationMinutes}-minute Zoom call · We'll match you with the right advisor · All times shown in your time zone`;

  return (
    <div className="slot-picker">
      <div className="slot-picker-header">
        <button
          type="button"
          className="link-back"
          onClick={onBack}
          aria-label="Back"
        >
          ← Back
        </button>
        <div>
          <h2 className="slot-picker-title">{titleText}</h2>
          <p className="slot-picker-sub">{subText}</p>
        </div>
        <div className="slot-picker-header-right">
          <label className="slot-picker-tz">
            <span className="slot-picker-tz-label">Time zone</span>
            <select
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              aria-label="Time zone for displayed times"
            >
              {US_TIMEZONES.map((t) => (
                <option key={t.tz} value={t.tz}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="slot-picker-refresh"
            onClick={() => loadSlots('refresh')}
            disabled={refreshing}
            aria-label="Refresh available times"
            title="Refresh available times"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {load.status === 'ok' && lastUpdated !== null && (
        <p className="slot-picker-freshness" aria-live="polite">
          Times update automatically. Last refreshed{' '}
          {new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: tz,
            timeZoneName: 'short',
          }).format(new Date(lastUpdated))}
          .
        </p>
      )}

      {load.status === 'loading' && (
        <p className="slot-picker-msg">Loading available times…</p>
      )}

      {load.status === 'error' && (
        <p className="slot-picker-msg slot-picker-err">
          We couldn't load available times: {load.message}
        </p>
      )}

      {load.status === 'ok' && days.length === 0 && (
        <p className="slot-picker-msg">
          No open times in the next {windowDays} days. Reach out to{' '}
          <a href="mailto:scheduler@annuityassociation.com">
            scheduler@annuityassociation.com
          </a>{' '}
          and we'll find a time directly.
        </p>
      )}

      {load.status === 'ok' && days.length > 0 && (
        <div className="slot-picker-body">
          {months.length > 1 && (
            <div className="day-rail month-rail" role="tablist" aria-label="Jump to month">
              {months.map((m) => (
                <button
                  key={m.key}
                  role="tab"
                  type="button"
                  aria-selected={m.key === activeMonth}
                  className={`day-pill ${
                    m.key === activeMonth ? 'day-pill-active' : ''
                  }`}
                  onClick={() => selectMonth(m.key)}
                >
                  {m.label}
                  <span className="day-pill-count">{m.dayCount}</span>
                </button>
              ))}
            </div>
          )}

          <div className="day-rail" role="tablist" aria-label="Day">
            {visibleDays.map((day) => (
              <button
                key={day.dayKey}
                role="tab"
                type="button"
                aria-selected={day.dayKey === activeDay}
                className={`day-pill ${
                  day.dayKey === activeDay ? 'day-pill-active' : ''
                }`}
                onClick={() => setActiveDay(day.dayKey)}
              >
                {day.dayLabel}
                <span className="day-pill-count">{day.slots.length}</span>
              </button>
            ))}
          </div>

          {activeGroup && (
            <div className="time-grid" role="tabpanel">
              {activeGroup.slots.map(({ slot, timeLabel }) => (
                <button
                  key={slot.startUtc}
                  type="button"
                  className="time-slot"
                  onClick={() => onSelectSlot(slot, tz)}
                >
                  {timeLabel}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
